/* Hinweis-Zuender: opt-in layered fuse hiss, irregular crackle and a short boom.
   Count-driven intensity only. No downloads, music or sound on page load. */
(()=>{'use strict';
 const instances=new WeakMap();
 const css=`.hz-counter .hz-header{align-items:flex-start}
.hz-counter .hz-header-actions{display:flex;flex-direction:column;align-items:flex-end;gap:5px;flex:none}
.hz-counter .hz-sound-toggle{position:relative;display:inline-flex;align-items:center;justify-content:center;width:44px;height:44px;padding:0;border:0;border-radius:50%;background:transparent;color:var(--hz-ink);cursor:pointer;touch-action:manipulation;appearance:none;-webkit-appearance:none}
.hz-counter .hz-sound-toggle::before{content:"";position:absolute;inset:3px;border:1px solid #214956;border-radius:50%;pointer-events:none}
.hz-counter .hz-sound-toggle svg{display:block;width:20px;height:20px;pointer-events:none}
.hz-counter .hz-sound-toggle:hover::before{background:rgba(255,255,255,.13)}
.hz-counter .hz-sound-toggle:focus-visible{outline:3px solid var(--hz-ink);outline-offset:3px}
.hz-counter .hz-sound-toggle:disabled{opacity:.45;cursor:not-allowed}
.hz-counter .hz-sound-toggle [data-hz-sound-waves]{display:none}
.hz-counter .hz-sound-toggle[aria-pressed="true"] [data-hz-sound-waves]{display:inline}
.hz-counter .hz-sound-toggle[aria-pressed="true"] [data-hz-sound-muted]{display:none}`;

// Compact impact: sharp attack, textured low-mid body and short damped reflections.
// Built once after sound is enabled. No network requests or extra audio context.
function buildBoom(context){
 const rate=context.sampleRate,duration=1.35,length=Math.ceil(rate*duration);
 const buffer=context.createBuffer(1,length,rate),out=buffer.getChannelData(0);
 const dry=new Float32Array(length),reflection=new Float32Array(length);
 const tau=Math.PI*2,fastA=1-Math.exp(-tau*5400/rate),bassA=1-Math.exp(-tau*170/rate);
 const reflectA=1-Math.exp(-tau*1450/rate),dcA=Math.exp(-tau*32/rate);
 let seed=0x48a5b91d,air=0,body=0,bass=0,phase=0,room=0;
 function random(){seed^=seed<<13;seed^=seed>>>17;seed^=seed<<5;return (seed>>>0)/2147483648-1;}
 for(let i=0;i<length;i++){
  const t=i/rate,n=random();
  air+=fastA*(n-air);
  const bodyA=1-Math.exp(-tau*(380+2600*Math.exp(-t/.11))/rate);
  body+=bodyA*(n-body);bass+=bassA*(random()-bass);
  // Very fast pitch settling avoids a long electronic downward sweep.
  phase+=tau*(62+82*Math.exp(-t/.024))/rate;
  const attack=Math.min(1,t/.0007);
  const crack=.8*air*attack*Math.exp(-t/.019);
  const punch=1.35*body*(1-Math.exp(-t/.0015))*(.82*Math.exp(-t/.125)+.18*Math.exp(-t/.32));
  const weight=.31*(Math.sin(phase)+.24*Math.sin(2*phase)+.10*Math.sin(3*phase))*(1-Math.exp(-t/.002))*Math.exp(-t/.155);
  const rumble=1.65*bass*(1-Math.exp(-t/.009))*Math.exp(-t/.29)*(1+.15*Math.sin(tau*23*t));
  // Gentle saturation binds the layers; the final peak is normalized below.
  dry[i]=Math.tanh((crack+punch+weight+rumble)*1.15);
  room+=reflectA*(dry[i]-room);reflection[i]=room;
 }
 // Uneven, progressively quieter reflections, baked into the stoppable voice.
 const taps=[[.031,.17],[.053,.13],[.083,.10],[.127,.075],[.191,.05],[.271,.03],[.359,.018]]
  .map(([delay,gain])=>[Math.round(delay*rate),gain]);
 let previousInput=0,previousOutput=0,peak=0;
 for(let i=0;i<length;i++){
  let sample=dry[i];for(const [delay,gain] of taps)if(i>=delay)sample+=reflection[i-delay]*gain;
  const filtered=sample-previousInput+dcA*previousOutput;
  previousInput=sample;previousOutput=filtered;
  const fade=Math.min(1,(length-1-i)/(rate*.07));
  out[i]=filtered*Math.max(0,fade);peak=Math.max(peak,Math.abs(out[i]));
 }
 const scale=peak>0 ? .86/peak : 0;
 for(let i=0;i<length;i++)out[i]*=scale;
 return buffer;
}

 function init(root){
  if(instances.has(root))return;
  const back=root.querySelector('.hz-header .hz-back');if(!back)return;
  if(!document.getElementById('hz-sound-styles')){
   const style=document.createElement('style');style.id='hz-sound-styles';style.textContent=css;document.head.append(style);
  }
  const wrapper=document.createElement('div');wrapper.className='hz-header-actions';back.before(wrapper);wrapper.append(back);
  const button=document.createElement('button');button.type='button';button.className='hz-sound-toggle';
  button.setAttribute('data-hz-sound','');button.setAttribute('aria-label','Ton');
  button.innerHTML='<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M3 9h4l5-4v14l-5-4H3Z"/><path data-hz-sound-muted d="m17 9 5 6m0-6-5 6"/><g data-hz-sound-waves><path d="M16 8a6 6 0 0 1 0 8M19 5a10 10 0 0 1 0 14"/></g></svg>';
  wrapper.append(button);
  const Audio=window.AudioContext||window.webkitAudioContext;
  let enabled=false,context=null,master=null,white=null,brown=null,boom=null;
  let destroyed=false,away=false,epoch=0,resuming=null,fuse=null,fuseTimer=0,nextCrackle=0;
  const voices=new Set();
  const snapshot=()=>({phase:root.dataset.phase,count:Number(root.dataset.count)||0,fx:root.dataset.fx||''});
  let previous=snapshot();
  const allowed=()=>enabled&&!destroyed&&!away&&!document.hidden;
  const audible=()=>allowed()&&context?.state==='running';
  const intensity=()=>Math.max(0,Math.min(1,(snapshot().count-9)/5));
  const wantsFuse=()=>audible()&&root.dataset.phase==='active'&&snapshot().count<15;
  function paint(){
   button.setAttribute('aria-pressed',String(enabled));
   button.title=Audio?(enabled?'Ton ausschalten':'Ton einschalten'):'Ton wird in diesem Browser nicht unterst\u00fctzt';
  }
  function disconnect(nodes){for(const node of nodes){try{node.disconnect();}catch(_){}}}
  function stopVoices(){for(const voice of [...voices]){try{voice.source.stop();}catch(_){}voice.clean();}}
  function stopFuse(){
   clearTimeout(fuseTimer);fuseTimer=0;nextCrackle=0;
   if(!fuse)return;
   const old=fuse;fuse=null;
   for(const source of old.sources){try{source.stop();}catch(_){}}
   disconnect(old.nodes);
  }
  function stopAll(){epoch++;stopFuse();stopVoices();}
  function suspend(){stopAll();if(context&&context.state!=='closed')context.suspend().catch(()=>{});}
  function makeBuffers(){
   const rate=context.sampleRate;
   white=context.createBuffer(1,Math.ceil(rate*4.1),rate);
   brown=context.createBuffer(1,Math.ceil(rate*1.4),rate);
   const w=white.getChannelData(0),b=brown.getChannelData(0);
   for(let i=0;i<w.length;i++)w[i]=Math.random()*2-1;
   let low=0;for(let i=0;i<b.length;i++){low=.975*low+.025*(Math.random()*2-1);b[i]=Math.tanh(low*6);}
   boom=buildBoom(context);
  }
  // Context creation/resumption follows the user's sound switch or game control.
  function unlock(){
   if(!allowed()||!Audio)return Promise.resolve(false);
   try{
    if(!context||context.state==='closed'){
     context=new Audio({latencyHint:'interactive'});
     master=context.createGain();master.gain.value=.55;
     const compressor=context.createDynamicsCompressor();
     compressor.threshold.value=-12;compressor.knee.value=12;compressor.ratio.value=6;
     compressor.attack.value=.003;compressor.release.value=.12;
     const highpass=context.createBiquadFilter();highpass.type='highpass';highpass.frequency.value=30;highpass.Q.value=.5;
     master.connect(highpass);highpass.connect(compressor);compressor.connect(context.destination);makeBuffers();
    }
    if(context.state==='running')return Promise.resolve(true);
    const version=epoch;
    resuming=context.resume().then(()=>version===epoch&&allowed()&&context?.state==='running').catch(()=>false);
    return resuming;
   }catch(_){return Promise.resolve(false);}
  }
  function envelope(gain,t,duration,peak,attack=.004){
   gain.gain.setValueAtTime(.0001,t);
   gain.gain.exponentialRampToValueAtTime(Math.max(.0002,peak),t+attack);
   gain.gain.exponentialRampToValueAtTime(.0001,t+duration);
  }
  function track(source,nodes,t,duration,offset){
   let cleaned=false;
   const voice={source,clean(){
    if(cleaned)return;cleaned=true;voices.delete(voice);source.onended=null;disconnect([source,...nodes]);
   }};
   voices.add(voice);source.onended=voice.clean;
   if(offset===undefined)source.start(t);else source.start(t,offset);
   source.stop(t+duration+.025);
  }
  function noise(t,duration,peak,type,frequency,endFrequency,buffer=white,attack=.004){
   const source=context.createBufferSource(),filter=context.createBiquadFilter(),gain=context.createGain();
   source.buffer=buffer;filter.type=type;filter.Q.value=.65;
   filter.frequency.setValueAtTime(frequency,t);filter.frequency.exponentialRampToValueAtTime(endFrequency,t+duration);
   envelope(gain,t,duration,peak,attack);
   source.connect(filter);filter.connect(gain);gain.connect(master);
   const offset=Math.random()*Math.max(0,buffer.duration-duration-.05);
   track(source,[filter,gain],t,duration,offset);
  }
  function tone(t,duration,peak,frequency,endFrequency,type='sine'){
   const source=context.createOscillator(),gain=context.createGain();source.type=type;
   source.frequency.setValueAtTime(frequency,t);source.frequency.exponentialRampToValueAtTime(endFrequency,t+duration);
   envelope(gain,t,duration,peak);source.connect(gain);gain.connect(master);track(source,[gain],t,duration);
  }
  function crackle(t,peak=.06){
   // Tiny filtered bursts, not an electronic beep. Each burst varies slightly.
   const f=2100+Math.random()*1800;
   noise(t,.018+Math.random()*.026,peak,'bandpass',f,f*.65,white,.0015);
   if(Math.random()<.35)noise(t+.025,.022,peak*.48,'highpass',1900,1300,white,.0015);
  }
  function modulate(){
   fuseTimer=0;if(!wantsFuse()){stopFuse();return;}
   if(!fuse)return;
   const t=context.currentTime,level=intensity(),variation=.88+Math.random()*.24;
   // Only the hint count changes the baseline; random motion adds texture.
   fuse.warm.gain.gain.setTargetAtTime((.105+level*.032)*variation,t,.14);
   fuse.air.gain.gain.setTargetAtTime((.026+level*.010)*(.85+Math.random()*.3),t,.17);
   fuse.warm.filter.frequency.setTargetAtTime(1700+level*380+Math.random()*180,t,.2);
   if(t>=nextCrackle){
    if(voices.size<18)crackle(t+.006,(.042+level*.022)*(.65+Math.random()*.55));
    nextCrackle=t+(.19+Math.random()*.38)*(1-level*.32);
   }
   fuseTimer=window.setTimeout(modulate,110);
  }
  function syncFuse(){
   if(!wantsFuse()){stopFuse();return;}
   if(fuse)return;
   const t=context.currentTime,parts=[];
   function layer(type,frequency,lowpass,level){
    const source=context.createBufferSource(),filter=context.createBiquadFilter(),soften=context.createBiquadFilter(),gain=context.createGain();
    source.buffer=white;source.loop=true;source.loopStart=0;source.loopEnd=white.duration;
    filter.type=type;filter.frequency.value=frequency;filter.Q.value=.55;
    soften.type='lowpass';soften.frequency.value=lowpass;soften.Q.value=.55;
    gain.gain.setValueAtTime(0,t);gain.gain.setTargetAtTime(level,t,.065);
    source.connect(filter);filter.connect(soften);soften.connect(gain);gain.connect(master);
    source.start(t,Math.random()*white.duration);parts.push(source,filter,soften,gain);
    return {source,filter,gain};
   }
   const warm=layer('bandpass',1700,4200,.10),air=layer('highpass',3000,5900,.025);
   fuse={warm,air,sources:[warm.source,air.source],nodes:parts};
   nextCrackle=t+.12+Math.random()*.15;modulate();
  }
  function synth(kind){
   if(!audible())return;
   if(voices.size>24)stopVoices();
   const t=context.currentTime+.005,level=intensity();
   if(kind==='ignite'){
    noise(t,.065,.22,'bandpass',3200,1900,white,.002);
    noise(t+.025,.34,.27,'bandpass',3800,1600,white,.012);
    crackle(t+.07,.10);
   }else if(kind==='boom'){
    stopFuse();stopVoices();
    const source=context.createBufferSource();
    source.buffer=boom;source.connect(master);
    track(source,[],t,boom.duration,0);
   }else if(kind==='spark'){
    noise(t,.07,.16+level*.045,'bandpass',3300,1800,white,.002);
    crackle(t+.025,.09+level*.02);
   }else{
    noise(t,.045,.105,'bandpass',2600,1500,white,.002);
   }
  }
  function play(kind){
   if(!allowed())return;
   if(audible()){synth(kind);return;}
   const version=epoch,at=performance.now();
   resuming?.then(ok=>{if(ok&&version===epoch&&performance.now()-at<200)synth(kind);}).catch(()=>{});
  }
  function resumeFuse(){
   if(!allowed())return;
   const version=epoch;
   unlock().then(ok=>{if(ok&&version===epoch)syncFuse();});
  }
  function toggle(){
   enabled=!enabled;stopAll();paint();
   if(!enabled){suspend();return;}
   const version=epoch;
   unlock().then(ok=>{
    if(destroyed||!enabled||version!==epoch)return;
    if(ok){play('confirm');syncFuse();}
    else{enabled=false;paint();button.title='Ton nicht verf\u00fcgbar. Zum erneuten Versuchen klicken.';}
   });
  }
  function gesture(event){
   const target=event.target instanceof Element?event.target.closest('[data-hz-primary],[data-hz-reset]'):null;
   if(!target||!root.contains(target)||target.disabled)return;
   if(target.matches('[data-hz-reset]')){stopAll();return;}
   if(enabled)resumeFuse();
  }
  const observer=new MutationObserver(()=>{
   const state=snapshot(),before=previous;previous=state;
   if(state.phase==='ready'){if(before.phase!=='ready')stopAll();return;}
   if(state.fx==='exploding'&&before.fx!=='exploding'){play('boom');return;}
   if(state.phase==='finished'){
    stopFuse();
    if(before.phase!=='finished'&&!root.querySelector('.hz-fx-ready'))play('boom');
    return;
   }
   if(state.phase==='active'){
    syncFuse();
    if(state.count===0&&before.phase!=='active')play('ignite');
    else if(before.phase==='active'&&state.count>before.count&&state.count<15)play('spark');
   }
  });
  observer.observe(root,{attributes:true,attributeFilter:['data-phase','data-count','data-fx']});
  function visibility(){if(document.hidden)suspend();else if(root.dataset.phase==='active')resumeFuse();}
  function hide(){away=true;suspend();}
  function show(){away=false;if(root.dataset.phase==='active')resumeFuse();}
  button.disabled=!Audio;paint();
  button.addEventListener('click',toggle);root.addEventListener('click',gesture,true);
  document.addEventListener('visibilitychange',visibility);window.addEventListener('pagehide',hide);window.addEventListener('pageshow',show);
  instances.set(root,()=>{
   destroyed=true;enabled=false;stopAll();observer.disconnect();
   button.removeEventListener('click',toggle);root.removeEventListener('click',gesture,true);
   document.removeEventListener('visibilitychange',visibility);window.removeEventListener('pagehide',hide);window.removeEventListener('pageshow',show);
   if(context&&context.state!=='closed')context.close().catch(()=>{});
   wrapper.before(back);wrapper.remove();instances.delete(root);
  });
 }
 function roots(scope){return [...(scope.matches?.('[data-hz-counter]')?[scope]:[]),...scope.querySelectorAll('[data-hz-counter]')];}
 function mount(scope){roots(scope).forEach(init);}
 if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>mount(document),{once:true});else mount(document);
 document.addEventListener('shopify:section:load',event=>mount(event.target));
 document.addEventListener('shopify:section:unload',event=>roots(event.target).forEach(root=>instances.get(root)?.()));
})();
